/*!
 * vue-control-descriptor v0.10.17 
 * (c) 2020 Alex Tkachenko
 * Released under the MIT License.
 */
import { Tag } from '@aspectus/vue-tag';
import { StylingFunctionalMixin } from '@aspectus/vue-bem-styling';
import { Hint } from '@aspectus/vue-control-hint';
import { Label } from '@aspectus/vue-control-label';
import { withDefaultProps } from '@aspectus/vue-utils';

//
var script = {
  name: 'control-descriptor-layout',
  mixins: [StylingFunctionalMixin],
  components: {
    Tag: Tag
  },
  props: {
    blockName: {
      type: String,
      default: 'control-descriptor'
    }
  }
};

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier
/* server only */
, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
  if (typeof shadowMode !== 'boolean') {
    createInjectorSSR = createInjector;
    createInjector = shadowMode;
    shadowMode = false;
  } // Vue.extend constructor export interop.


  var options = typeof script === 'function' ? script.options : script; // render functions

  if (template && template.render) {
    options.render = template.render;
    options.staticRenderFns = template.staticRenderFns;
    options._compiled = true; // functional template

    if (isFunctionalTemplate) {
      options.functional = true;
    }
  } // scopedId


  if (scopeId) {
    options._scopeId = scopeId;
  }

  var hook;

  if (moduleIdentifier) {
    // server build
    hook = function hook(context) {
      // 2.3 injection
      context = context || // cached call
      this.$vnode && this.$vnode.ssrContext || // stateful
      this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext; // functional
      // 2.2 with runInNewContext: true

      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      } // inject component styles


      if (style) {
        style.call(this, createInjectorSSR(context));
      } // register component module identifier for async chunk inference


      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    }; // used by ssr in case component is cached and beforeCreate
    // never gets called


    options._ssrRegister = hook;
  } else if (style) {
    hook = shadowMode ? function (context) {
      style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
    } : function (context) {
      style.call(this, createInjector(context));
    };
  }

  if (hook) {
    if (options.functional) {
      // register for functional component in vue file
      var originalRender = options.render;

      options.render = function renderWithStyleInjection(h, context) {
        hook.call(context);
        return originalRender(h, context);
      };
    } else {
      // inject component registration as beforeCreate hook
      var existing = options.beforeCreate;
      options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
    }
  }

  return script;
}

var isOldIE = typeof navigator !== 'undefined' && /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function(_h, _vm) {
  var _c = _vm._c;
  return _c(
    "tag",
    _vm._g(
      _vm._b(
        { class: [_vm.data.class, _vm.data.staticClass, _vm.props.bclass()] },
        "tag",
        _vm.data.attrs,
        false
      ),
      _vm.data.on
    ),
    [
      _c(
        "transition",
        { attrs: { name: _vm.props.blockName + "__label--transition" } },
        [
          _vm.scopedSlots.label
            ? _c(
                "div",
                { class: _vm.props.eclass("label") },
                [_vm._t("label")],
                2
              )
            : _vm._e()
        ]
      ),
      _vm._v(" "),
      _c(
        "transition",
        { attrs: { name: _vm.props.blockName + "__element--transition" } },
        [
          _vm.scopedSlots.element
            ? _c(
                "div",
                { class: _vm.props.eclass("element") },
                [_vm._t("element")],
                2
              )
            : _vm._e()
        ]
      ),
      _vm._v(" "),
      _c(
        "transition",
        { attrs: { name: _vm.props.blockName + "__meta--transition" } },
        [
          _vm.scopedSlots.meta
            ? _c(
                "div",
                { class: _vm.props.eclass("meta") },
                [_vm._t("meta")],
                2
              )
            : _vm._e()
        ]
      ),
      _vm._v(" "),
      _c(
        "transition",
        { attrs: { name: _vm.props.blockName + "__hint--transition" } },
        [
          _vm.scopedSlots.hint
            ? _c(
                "div",
                { class: _vm.props.eclass("hint") },
                [_vm._t("hint")],
                2
              )
            : _vm._e()
        ]
      ),
      _vm._v(" "),
      _c(
        "transition",
        { attrs: { name: _vm.props.blockName + "__errors--transition" } },
        [
          _vm.scopedSlots.errors
            ? _c(
                "div",
                { class: _vm.props.eclass("errors") },
                [_vm._t("errors")],
                2
              )
            : _vm._e()
        ]
      )
    ],
    1
  )
};
var __vue_staticRenderFns__ = [];
__vue_render__._withStripped = true;

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = true;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = normalizeComponent(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

//
var hintComponent = withDefaultProps({
  styling: 'default',
  variant: 'hint'
})(Hint);
var errorComponent = withDefaultProps({
  styling: 'default',
  variant: 'error'
})(Hint);
var RESERVED_SLOTS = {
  label: true,
  hint: true,
  errors: true,
  element: true,
  meta: true
};
var DESCRIPTOR_ELEMENT_SLOT_PREFIX = 'element-';
var DESCRIPTOR_ELEMENT_SLOT_PREFIX_LENGTH = DESCRIPTOR_ELEMENT_SLOT_PREFIX.length;

var eSlotsResolver = function eSlotsResolver(acc, key) {
  if (key.startsWith(DESCRIPTOR_ELEMENT_SLOT_PREFIX)) {
    acc[key] = key.slice(DESCRIPTOR_ELEMENT_SLOT_PREFIX_LENGTH);
  } else {
    acc[key] = key;
  }

  return acc;
};

var getESlots = function getESlots(keys) {
  return keys.filter(function (key) {
    return !RESERVED_SLOTS[key];
  }).reduce(eSlotsResolver, {});
};

var script$1 = {
  name: 'control-descriptor',
  inheritAttrs: false,
  components: {
    Tag: Tag
  },
  props: {
    element: {},
    tag: {
      default: 'div'
    },
    labelComponent: {
      default: function _default() {
        return Label;
      }
    },
    layoutComponent: {
      default: function _default() {
        return __vue_component__;
      }
    },
    hintComponent: {
      default: function _default() {
        return hintComponent;
      }
    },
    errorComponent: {
      default: function _default() {
        return errorComponent;
      }
    },
    layoutProps: Object,
    labelText: String,
    hintText: String,
    disabled: Boolean,
    required: Boolean,
    readonly: Boolean,
    errors: Array,
    // HACK: Tricky hack for vee-validate provider v-model-detection
    value: {}
  },
  computed: {
    hasErrors: function hasErrors() {
      return !!(this.errors && this.errors.length);
    },
    passedParameters: function passedParameters() {
      return {
        disabled: this.disabled,
        required: this.required,
        readonly: this.readonly,
        invalid: this.hasErrors
      };
    },
    lProps: function lProps() {
      return Object.assign({}, this.passedParameters, this.layoutProps || {});
    },
    eProps: function eProps() {
      return Object.assign({}, this.passedParameters, {
        element: this.element,
        value: this.value,
        attrs: this.$attrs,
        listeners: this.$listeners
      });
    },
    eSlots: function eSlots() {
      return getESlots(Object.keys(this.$scopedSlots));
    }
  }
};

/* script */
const __vue_script__$1 = script$1;

/* template */
var __vue_render__$1 = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    _vm.layoutComponent,
    _vm._b(
      {
        tag: "component",
        attrs: { tag: _vm.tag },
        scopedSlots: _vm._u(
          [
            _vm.$scopedSlots.label || _vm.labelText
              ? {
                  key: "label",
                  fn: function() {
                    return [
                      _vm._t(
                        "label",
                        [
                          _c(_vm.labelComponent, { tag: "component" }, [
                            _vm._v(_vm._s(_vm.labelText))
                          ])
                        ],
                        { labelText: _vm.labelText },
                        _vm.passedParameters
                      )
                    ]
                  },
                  proxy: true
                }
              : null,
            _vm.$scopedSlots.hint || _vm.hintText
              ? {
                  key: "hint",
                  fn: function() {
                    return [
                      _vm._t(
                        "hint",
                        [
                          _c(_vm.hintComponent, { tag: "component" }, [
                            _vm._v(_vm._s(_vm.hintText))
                          ])
                        ],
                        { hintText: _vm.hintText },
                        _vm.passedParameters
                      )
                    ]
                  },
                  proxy: true
                }
              : null,
            _vm.$scopedSlots.errors || _vm.hasErrors
              ? {
                  key: "errors",
                  fn: function() {
                    return [
                      _vm._t(
                        "errors",
                        _vm._l(_vm.errors, function(error, i) {
                          return _c(
                            _vm.errorComponent,
                            { key: i, tag: "component" },
                            [_vm._v("\n        " + _vm._s(error) + "\n      ")]
                          )
                        }),
                        { errors: _vm.errors },
                        _vm.passedParameters
                      )
                    ]
                  },
                  proxy: true
                }
              : null,
            _vm.$scopedSlots.element || _vm.element
              ? {
                  key: "element",
                  fn: function() {
                    return [
                      _vm._t(
                        "element",
                        [
                          _c(
                            "tag",
                            _vm._g(
                              _vm._b(
                                {
                                  attrs: {
                                    tag: _vm.element,
                                    disabled: _vm.disabled,
                                    required: _vm.required,
                                    readonly: _vm.readonly,
                                    invalid: _vm.hasErrors,
                                    value: _vm.value,
                                    "enable-vue-tag-props-fix": true
                                  },
                                  scopedSlots: _vm._u(
                                    [
                                      _vm._l(_vm.eSlots, function(name, key) {
                                        return {
                                          key: name,
                                          fn: function(binded) {
                                            return [
                                              _vm._t(key, null, null, binded)
                                            ]
                                          }
                                        }
                                      })
                                    ],
                                    null,
                                    true
                                  )
                                },
                                "tag",
                                _vm.$attrs,
                                false
                              ),
                              _vm.$listeners
                            )
                          )
                        ],
                        null,
                        _vm.eProps
                      )
                    ]
                  },
                  proxy: true
                }
              : null,
            _vm.$scopedSlots.meta
              ? {
                  key: "meta",
                  fn: function() {
                    return [
                      _vm._t(
                        "meta",
                        null,
                        {
                          labelText: _vm.labelText,
                          hintText: _vm.hintText,
                          errors: _vm.errors,
                          value: _vm.value
                        },
                        _vm.passedParameters
                      )
                    ]
                  },
                  proxy: true
                }
              : null
          ],
          null,
          true
        )
      },
      "component",
      _vm.lProps,
      false
    )
  )
};
var __vue_staticRenderFns__$1 = [];
__vue_render__$1._withStripped = true;

  /* style */
  const __vue_inject_styles__$1 = undefined;
  /* scoped */
  const __vue_scope_id__$1 = undefined;
  /* module identifier */
  const __vue_module_identifier__$1 = undefined;
  /* functional template */
  const __vue_is_functional_template__$1 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1 = normalizeComponent(
    { render: __vue_render__$1, staticRenderFns: __vue_staticRenderFns__$1 },
    __vue_inject_styles__$1,
    __vue_script__$1,
    __vue_scope_id__$1,
    __vue_is_functional_template__$1,
    __vue_module_identifier__$1,
    false,
    undefined,
    undefined,
    undefined
  );

function install(Vue) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$name = _ref.name,
      name = _ref$name === void 0 ? __vue_component__$1.name : _ref$name,
      _ref$layoutName = _ref.layoutName,
      layoutName = _ref$layoutName === void 0 ? __vue_component__.name : _ref$layoutName;

  Vue.component(name, __vue_component__$1);
  Vue.component(layoutName, __vue_component__);
}
var main = {
  ControlDescriptor: __vue_component__$1,
  ControlDescriptorLayout: __vue_component__,
  install: install
};

export default main;
export { __vue_component__$1 as ControlDescriptor, __vue_component__ as ControlDescriptorLayout, install };



// WEBPACK FOOTER //
// ./node_modules/@aspectus/vue-control-descriptor/dist/vue-control-descriptor.esm.js